'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import type { GetInteractionAggregatesReqFiltersV2Item, TimeRangeConfig } from '@/api/generated/model';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { useAiProficiencyEffectiveNow, useAiProficiencyStableNow } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-stable-now';
import { useUserDimensionMapping } from '@/domains/pages/shared/use-user-dimension-mapping';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS, getAiProficiencyTimeRangeQueryKey } from '@/domains/pages/ai-proficiency/query-options';
import { isQueryCacheMissing } from '@/domains/pages/ai-proficiency/utils/query-loading';
import { buildSegmentFilterV2Values, buildSegmentGroupBy, segmentKindLabel, type SegmentKind } from '@/domains/pages/ai-proficiency/utils/segment-api-payloads';
import { useModelSavingsTasksQuery } from '@/domains/pages/model-savings/hooks/use-model-savings-tasks-query';
import { buildModelSavingsActualTierGroupBy, buildModelSavingsAnnualTimeRange, buildModelSavingsOptimalTierGroupBy, MODEL_SAVINGS_SEGMENT_VARIABLES, MODEL_SAVINGS_SUMMARY_VARIABLES, MODEL_SAVINGS_TIER_MIX_VARIABLES, MODEL_SAVINGS_TOP_OPPORTUNITIES_LIMIT, MODEL_SAVINGS_TOP_TASKS_CARD_LIMIT, type ModelSavingsTasksQueryScope } from '@/domains/pages/model-savings/utils/model-savings-api';
import { EMPTY_SAVINGS_SCOPE_DATA, mapModelSavingsScopeData, mapModelSavingsSegmentSummaries } from '@/domains/pages/model-savings/utils/map-model-savings-aggregates';
import type { ModelSavingsView, SavingsSegmentSummary, SavingsTask } from '@/domains/pages/model-savings/types';
import { applySavingsSegmentDisplayNames, getSavingsSegmentDisplayName } from '@/domains/pages/model-savings/utils/model-savings-segment-display';
import { sortSegmentsBySaving } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { UserDimension } from '@/api/generated/model';
import { useCallback, useMemo } from 'react';
export type { ModelSavingsView };
export type ModelSavingsPageData = {
  view: ModelSavingsView;
  segmentKind: SegmentKind | null;
  segmentNoun: string;
  segmentNounPlural: string;
  selectedSegmentName: string;
  segments: SavingsSegmentSummary[];
  scopeData: ReturnType<typeof mapModelSavingsScopeData>;
  topTasks: SavingsTask[];
  topOpportunitiesTasks: SavingsTask[];
  tasksQueryScope: ModelSavingsTasksQueryScope | null;
  scopeReady: boolean;
  annualTimeRange: TimeRangeConfig | undefined;
  tasksTotal: number;
  isInitialLoading: boolean;
  isChartsLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  formatSegmentName: (name: string) => string;
};
type UseModelSavingsPageDataParams = {
  projectId: string | undefined;
  view: ModelSavingsView;
  selectedSegmentName: string | undefined;
};
function buildScopeFilters(view: ModelSavingsView, selectedSegmentName: string): GetInteractionAggregatesReqFiltersV2Item[] {
  if (view === 'all' || !selectedSegmentName) return [];
  return [buildSegmentFilterV2Values(view, [selectedSegmentName])];
}
export function useModelSavingsPageData(t0) {
  const $ = _c(125);
  const {
    projectId,
    view,
    selectedSegmentName
  } = t0;
  const stableNow = useAiProficiencyStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useAiProficiencyEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  let t1;
  if ($[0] !== effectiveNow || $[1] !== stableNow) {
    t1 = buildModelSavingsAnnualTimeRange(stableNow, effectiveNow);
    $[0] = effectiveNow;
    $[1] = stableNow;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const annualTimeRange = t1;
  let t2;
  if ($[3] !== annualTimeRange) {
    t2 = getAiProficiencyTimeRangeQueryKey(annualTimeRange);
    $[3] = annualTimeRange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const annualTimeRangeQueryKey = t2;
  const enabled = Boolean(projectId && annualTimeRange && isEffectiveNowReady);
  const segmentKind = view === "all" ? null : view;
  const {
    hasMapping
  } = useUserDimensionMapping(segmentKind ?? UserDimension.team, enabled && segmentKind != null);
  let t3;
  if ($[5] !== segmentKind) {
    t3 = segmentKind ? segmentKindLabel(segmentKind) : "org";
    $[5] = segmentKind;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const segmentNoun = t3;
  let t4;
  if ($[7] !== segmentKind) {
    t4 = segmentKind ? segmentKindLabel(segmentKind, true) : "org";
    $[7] = segmentKind;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const segmentNounPlural = t4;
  let t5;
  if ($[9] !== annualTimeRangeQueryKey || $[10] !== projectId || $[11] !== segmentKind) {
    t5 = [QUERY_KEY_INTERACTION_AGGREGATES, "model-savings-segments", segmentKind, projectId, annualTimeRangeQueryKey];
    $[9] = annualTimeRangeQueryKey;
    $[10] = projectId;
    $[11] = segmentKind;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== annualTimeRange || $[14] !== projectId || $[15] !== segmentKind) {
    t6 = t7 => {
      const {
        signal
      } = t7;
      if (!segmentKind) {
        throw new Error("Segment list query requires team, market, or tenure");
      }
      return getInteractionAggregates({
        project_id: projectId,
        time_range: annualTimeRange,
        filters: [],
        variables: MODEL_SAVINGS_SEGMENT_VARIABLES,
        group_by: buildSegmentGroupBy(segmentKind),
        limit: 100
      }, {
        signal
      });
    };
    $[13] = annualTimeRange;
    $[14] = projectId;
    $[15] = segmentKind;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = enabled && segmentKind != null;
  let t8;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const segmentsQuery = useQuery(t8);
  let t9;
  if ($[21] !== hasMapping || $[22] !== segmentKind || $[23] !== segmentsQuery.data) {
    const raw = segmentsQuery.data ? mapModelSavingsSegmentSummaries(segmentsQuery.data) : [];
    t9 = applySavingsSegmentDisplayNames(sortSegmentsBySaving(raw), hasMapping, segmentKind);
    $[21] = hasMapping;
    $[22] = segmentKind;
    $[23] = segmentsQuery.data;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const segments = t9;
  let t10;
  if ($[25] !== hasMapping || $[26] !== segmentKind || $[27] !== segments) {
    t10 = name => getSavingsSegmentDisplayName(name, segments, hasMapping, segmentKind);
    $[25] = hasMapping;
    $[26] = segmentKind;
    $[27] = segments;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const formatSegmentName = t10;
  let t11;
  bb0: {
    if (view === "all") {
      t11 = "";
      break bb0;
    }
    if (selectedSegmentName && segments.some(segment => segment.name === selectedSegmentName)) {
      t11 = selectedSegmentName;
      break bb0;
    }
    t11 = segments[0]?.name ?? "";
  }
  const resolvedSelectedName = t11;
  const segmentListFetched = view === "all" || segmentsQuery.isFetched;
  const hasNoSegments = view !== "all" && segmentListFetched && segments.length === 0;
  const scopeReady = enabled && !hasNoSegments && (view === "all" || segmentListFetched && Boolean(resolvedSelectedName));
  let t12;
  if ($[29] !== resolvedSelectedName || $[30] !== view) {
    t12 = buildScopeFilters(view, resolvedSelectedName);
    $[29] = resolvedSelectedName;
    $[30] = view;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const scopeFilters = t12;
  let t13;
  bb1: {
    if (!scopeReady || !projectId || !annualTimeRange) {
      t13 = null;
      break bb1;
    }
    let t14;
    if ($[32] !== annualTimeRange || $[33] !== projectId || $[34] !== scopeFilters) {
      t14 = {
        projectId,
        timeRange: annualTimeRange,
        scopeFilters
      };
      $[32] = annualTimeRange;
      $[33] = projectId;
      $[34] = scopeFilters;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    t13 = t14;
  }
  const tasksQueryScope = t13;
  let t14;
  if ($[36] !== annualTimeRangeQueryKey || $[37] !== projectId || $[38] !== resolvedSelectedName || $[39] !== view) {
    t14 = [QUERY_KEY_INTERACTION_AGGREGATES, "model-savings-summary", view, resolvedSelectedName, projectId, annualTimeRangeQueryKey];
    $[36] = annualTimeRangeQueryKey;
    $[37] = projectId;
    $[38] = resolvedSelectedName;
    $[39] = view;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== annualTimeRange || $[42] !== projectId || $[43] !== scopeFilters) {
    t15 = t16 => {
      const {
        signal: signal_0
      } = t16;
      return getInteractionAggregates({
        project_id: projectId,
        time_range: annualTimeRange,
        filters: [],
        filters_v2: scopeFilters,
        variables: MODEL_SAVINGS_SUMMARY_VARIABLES,
        limit: 1
      }, {
        signal: signal_0
      });
    };
    $[41] = annualTimeRange;
    $[42] = projectId;
    $[43] = scopeFilters;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== scopeReady || $[46] !== t14 || $[47] !== t15) {
    t16 = {
      queryKey: t14,
      queryFn: t15,
      enabled: scopeReady,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[45] = scopeReady;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const summaryQuery = useQuery(t16);
  let t17;
  if ($[49] !== annualTimeRangeQueryKey || $[50] !== projectId || $[51] !== resolvedSelectedName || $[52] !== view) {
    t17 = [QUERY_KEY_INTERACTION_AGGREGATES, "model-savings-actual-tier-mix", view, resolvedSelectedName, projectId, annualTimeRangeQueryKey];
    $[49] = annualTimeRangeQueryKey;
    $[50] = projectId;
    $[51] = resolvedSelectedName;
    $[52] = view;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== annualTimeRange || $[55] !== projectId || $[56] !== scopeFilters) {
    t18 = t19 => {
      const {
        signal: signal_1
      } = t19;
      return getInteractionAggregates({
        project_id: projectId,
        time_range: annualTimeRange,
        filters: [],
        filters_v2: scopeFilters,
        variables: MODEL_SAVINGS_TIER_MIX_VARIABLES,
        group_by: buildModelSavingsActualTierGroupBy(),
        limit: 10
      }, {
        signal: signal_1
      });
    };
    $[54] = annualTimeRange;
    $[55] = projectId;
    $[56] = scopeFilters;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== scopeReady || $[59] !== t17 || $[60] !== t18) {
    t19 = {
      queryKey: t17,
      queryFn: t18,
      enabled: scopeReady,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[58] = scopeReady;
    $[59] = t17;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const actualTierMixQuery = useQuery(t19);
  let t20;
  if ($[62] !== annualTimeRangeQueryKey || $[63] !== projectId || $[64] !== resolvedSelectedName || $[65] !== view) {
    t20 = [QUERY_KEY_INTERACTION_AGGREGATES, "model-savings-optimal-tier-mix", view, resolvedSelectedName, projectId, annualTimeRangeQueryKey];
    $[62] = annualTimeRangeQueryKey;
    $[63] = projectId;
    $[64] = resolvedSelectedName;
    $[65] = view;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== annualTimeRange || $[68] !== projectId || $[69] !== scopeFilters) {
    t21 = t22 => {
      const {
        signal: signal_2
      } = t22;
      return getInteractionAggregates({
        project_id: projectId,
        time_range: annualTimeRange,
        filters: [],
        filters_v2: scopeFilters,
        variables: MODEL_SAVINGS_TIER_MIX_VARIABLES,
        group_by: buildModelSavingsOptimalTierGroupBy(),
        limit: 10
      }, {
        signal: signal_2
      });
    };
    $[67] = annualTimeRange;
    $[68] = projectId;
    $[69] = scopeFilters;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  let t22;
  if ($[71] !== scopeReady || $[72] !== t20 || $[73] !== t21) {
    t22 = {
      queryKey: t20,
      queryFn: t21,
      enabled: scopeReady,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[71] = scopeReady;
    $[72] = t20;
    $[73] = t21;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  const optimalTierMixQuery = useQuery(t22);
  const t23 = projectId ?? "";
  let t24;
  if ($[75] !== annualTimeRange || $[76] !== scopeFilters || $[77] !== scopeReady || $[78] !== t23) {
    t24 = {
      projectId: t23,
      timeRange: annualTimeRange,
      scopeFilters,
      enabled: scopeReady,
      limit: MODEL_SAVINGS_TOP_OPPORTUNITIES_LIMIT,
      offset: 0,
      queryKeySuffix: "top-opportunities"
    };
    $[75] = annualTimeRange;
    $[76] = scopeFilters;
    $[77] = scopeReady;
    $[78] = t23;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  const topOpportunitiesQuery = useModelSavingsTasksQuery(t24);
  const topOpportunitiesTasks = topOpportunitiesQuery.tasks;
  let t25;
  if ($[80] !== topOpportunitiesTasks) {
    t25 = topOpportunitiesTasks.slice(0, MODEL_SAVINGS_TOP_TASKS_CARD_LIMIT);
    $[80] = topOpportunitiesTasks;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  const topTasks = t25;
  let t26;
  bb2: {
    if (!summaryQuery.data) {
      let t27;
      if ($[82] !== segments) {
        t27 = {
          ...EMPTY_SAVINGS_SCOPE_DATA,
          segments
        };
        $[82] = segments;
        $[83] = t27;
      } else {
        t27 = $[83];
      }
      t26 = t27;
      break bb2;
    }
    let t27;
    if ($[84] !== actualTierMixQuery.data?.data) {
      t27 = actualTierMixQuery.data?.data ?? [];
      $[84] = actualTierMixQuery.data?.data;
      $[85] = t27;
    } else {
      t27 = $[85];
    }
    let t28;
    if ($[86] !== optimalTierMixQuery.data?.data) {
      t28 = optimalTierMixQuery.data?.data ?? [];
      $[86] = optimalTierMixQuery.data?.data;
      $[87] = t28;
    } else {
      t28 = $[87];
    }
    let t29;
    if ($[88] !== segments || $[89] !== summaryQuery.data || $[90] !== t27 || $[91] !== t28 || $[92] !== topOpportunitiesTasks) {
      t29 = mapModelSavingsScopeData(summaryQuery.data, topOpportunitiesTasks, segments, t27, t28);
      $[88] = segments;
      $[89] = summaryQuery.data;
      $[90] = t27;
      $[91] = t28;
      $[92] = topOpportunitiesTasks;
      $[93] = t29;
    } else {
      t29 = $[93];
    }
    t26 = t29;
  }
  const scopeData = t26;
  let t27;
  if ($[94] !== enabled || $[95] !== segmentsQuery || $[96] !== summaryQuery || $[97] !== view) {
    t27 = enabled && (view === "all" && isQueryCacheMissing(summaryQuery) || view !== "all" && isQueryCacheMissing(segmentsQuery));
    $[94] = enabled;
    $[95] = segmentsQuery;
    $[96] = summaryQuery;
    $[97] = view;
    $[98] = t27;
  } else {
    t27 = $[98];
  }
  const isInitialLoading = t27;
  const isChartsLoading = scopeReady && (summaryQuery.isLoading || actualTierMixQuery.isLoading || optimalTierMixQuery.isLoading || topOpportunitiesQuery.isLoading);
  const isError = segmentsQuery.isError || summaryQuery.isError || actualTierMixQuery.isError || optimalTierMixQuery.isError || topOpportunitiesQuery.isError;
  const error = segmentsQuery.error ?? summaryQuery.error ?? actualTierMixQuery.error ?? optimalTierMixQuery.error ?? topOpportunitiesQuery.error;
  let t28;
  if ($[99] !== actualTierMixQuery || $[100] !== optimalTierMixQuery || $[101] !== segmentsQuery || $[102] !== summaryQuery || $[103] !== topOpportunitiesQuery) {
    t28 = () => {
      segmentsQuery.refetch();
      summaryQuery.refetch();
      actualTierMixQuery.refetch();
      optimalTierMixQuery.refetch();
      topOpportunitiesQuery.refetch();
    };
    $[99] = actualTierMixQuery;
    $[100] = optimalTierMixQuery;
    $[101] = segmentsQuery;
    $[102] = summaryQuery;
    $[103] = topOpportunitiesQuery;
    $[104] = t28;
  } else {
    t28 = $[104];
  }
  const refetch = t28;
  let t29;
  if ($[105] !== annualTimeRange || $[106] !== error || $[107] !== formatSegmentName || $[108] !== isChartsLoading || $[109] !== isError || $[110] !== isInitialLoading || $[111] !== refetch || $[112] !== resolvedSelectedName || $[113] !== scopeData || $[114] !== scopeReady || $[115] !== segmentKind || $[116] !== segmentNoun || $[117] !== segmentNounPlural || $[118] !== segments || $[119] !== tasksQueryScope || $[120] !== topOpportunitiesQuery.total || $[121] !== topOpportunitiesTasks || $[122] !== topTasks || $[123] !== view) {
    t29 = {
      view,
      segmentKind,
      segmentNoun,
      segmentNounPlural,
      selectedSegmentName: resolvedSelectedName,
      segments,
      scopeData,
      topTasks,
      topOpportunitiesTasks,
      tasksQueryScope,
      scopeReady,
      annualTimeRange,
      tasksTotal: topOpportunitiesQuery.total,
      isInitialLoading,
      isChartsLoading,
      isError,
      error,
      refetch,
      formatSegmentName
    };
    $[105] = annualTimeRange;
    $[106] = error;
    $[107] = formatSegmentName;
    $[108] = isChartsLoading;
    $[109] = isError;
    $[110] = isInitialLoading;
    $[111] = refetch;
    $[112] = resolvedSelectedName;
    $[113] = scopeData;
    $[114] = scopeReady;
    $[115] = segmentKind;
    $[116] = segmentNoun;
    $[117] = segmentNounPlural;
    $[118] = segments;
    $[119] = tasksQueryScope;
    $[120] = topOpportunitiesQuery.total;
    $[121] = topOpportunitiesTasks;
    $[122] = topTasks;
    $[123] = view;
    $[124] = t29;
  } else {
    t29 = $[124];
  }
  return t29;
}